import { validateAvatarConfig, type AvatarConfig } from "@guandan/avatar-core";
import type { RevealedHands, RevealedHandsByRound } from "@guandan/protocol";
import { getSessionToken } from "./session";

export const reportReasons = [
  "不友好行为",
  "恶意拖延",
  "疑似作弊",
  "语音违规",
  "其他",
] as const;
export type ReportReason = (typeof reportReasons)[number];

export interface AuthoritativeHistoryMetrics {
  readonly won: boolean;
  readonly doubleUp: boolean;
  readonly doubleDown: boolean;
  readonly plays: number;
  readonly bombsPlayed: number;
  readonly trickRights: number;
  readonly managedTurns: number;
  readonly trusteeSeconds: number;
  readonly disconnects: number;
  readonly disconnected: boolean;
}

export interface AuthoritativeMatchHistory {
  readonly matchId: string;
  readonly roomId: string;
  readonly startedAt: number;
  readonly endedAt: number;
  readonly seat: 0 | 1 | 2 | 3;
  readonly team: 0 | 1;
  readonly finishOrder: 1 | 2 | 3 | 4;
  readonly levelDelta: number;
  readonly score: number;
  readonly metrics: AuthoritativeHistoryMetrics;
}

export interface AuthoritativeHistorySummary {
  readonly games: number;
  readonly currentRating: number;
  readonly peakRating: number;
  readonly currentRank: string;
  readonly highestRank: string;
  readonly recentTrend: readonly number[];
  readonly metrics: Omit<
    AuthoritativeHistoryMetrics,
    "won" | "doubleUp" | "doubleDown" | "disconnected"
  >;
}

export interface AuthoritativePlayerHistory {
  readonly matches: readonly AuthoritativeMatchHistory[];
  readonly summary: AuthoritativeHistorySummary | null;
}

export interface AuthoritativeBehaviorRates {
  readonly games: number;
  readonly trusteeGames: number;
  readonly disconnectedGames: number;
  readonly trusteeMatchRate: number;
  readonly disconnectMatchRate: number;
}

export interface ReplayEventPresentation {
  readonly seq: number;
  readonly type: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly createdAt: number;
}

export interface ReplayParticipantPresentation {
  readonly seat: 0 | 1 | 2 | 3;
  readonly nickname: string;
  readonly team: 0 | 1;
  readonly finishOrder: 1 | 2 | 3 | 4 | null;
  readonly isBot: boolean;
}

export interface ReplayPresentation {
  readonly source: "remote" | "local-practice";
  readonly matchId?: string;
  readonly selfSeat?: 0 | 1 | 2 | 3;
  readonly endedAt: number;
  readonly participants: readonly ReplayParticipantPresentation[];
  readonly authorityEvents: readonly ReplayEventPresentation[];
  readonly fallbackEvents: readonly string[];
  readonly revealedHandsByRound?: RevealedHandsByRound;
  readonly checksum?: string;
}

interface ReplayPlayerMetadata {
  readonly userId: string | null;
  readonly nickname: string;
  readonly seat: 0 | 1 | 2 | 3;
  readonly team: 0 | 1;
  readonly isBot: boolean;
  readonly botLevel: "standard" | "expert" | "master" | null;
}

export interface CompletedMatchReplay {
  readonly matchId: string;
  readonly events: readonly ReplayEventPresentation[];
  readonly players: readonly ReplayPlayerMetadata[];
  readonly revealedHandsByRound?: RevealedHandsByRound;
  readonly checksum: string;
}

export interface RemotePlayerProfile {
  readonly email: string;
  readonly nickname: string;
  readonly avatar: AvatarConfig;
  readonly level: number;
  readonly experience: number;
  readonly reputation: number;
}

export class PlayerDataApiError extends Error {
  constructor(
    message: string,
    readonly code:
      | "UNAVAILABLE"
      | "UNAUTHENTICATED"
      | "FORBIDDEN"
      | "NOT_FOUND"
      | "INVALID_REQUEST"
      | "INVALID_RESPONSE",
    readonly status = 0,
  ) {
    super(message);
    this.name = "PlayerDataApiError";
  }
}

export class PlayerDataClient {
  constructor(
    private readonly baseUrl: string,
    private readonly token: string,
    private readonly fetcher: typeof fetch = (input, init) =>
      fetch(input, init),
  ) {
    if (!/^https?:\/\//u.test(baseUrl) || token.length === 0)
      throw new Error("Player data client configuration is invalid");
  }

  async history(signal?: AbortSignal): Promise<AuthoritativePlayerHistory> {
    const payload = await this.request("/me/history?limit=100", {}, signal);
    return parsePlayerHistory(payload);
  }

  async replay(
    matchId: string,
    signal?: AbortSignal,
  ): Promise<CompletedMatchReplay> {
    if (matchId.length === 0 || matchId.length > 64)
      throw new PlayerDataApiError("回放编号无效", "INVALID_RESPONSE");
    const payload = await this.request(
      `/matches/${encodeURIComponent(matchId)}/replay`,
      {},
      signal,
    );
    const replay = parseCompletedMatchReplay(payload);
    if (replay.matchId !== matchId)
      throw new PlayerDataApiError("回放编号与请求不一致", "INVALID_RESPONSE");
    return replay;
  }

  async profile(signal?: AbortSignal): Promise<RemotePlayerProfile | null> {
    return parsePlayerProfile(await this.request("/me/profile", {}, signal));
  }

  async saveProfile(
    nickname: string,
    avatar: AvatarConfig,
    signal?: AbortSignal,
  ): Promise<RemotePlayerProfile> {
    const profileNickname = parseProfileNickname(nickname);
    const profileAvatar = parseProfileAvatar(avatar);
    const profile = parsePlayerProfile(
      await this.request(
        "/me/profile",
        {
          method: "PUT",
          body: JSON.stringify({
            nickname: profileNickname,
            avatar: profileAvatar,
          }),
        },
        signal,
      ),
    );
    if (!profile)
      throw new PlayerDataApiError("角色档案保存后仍为空", "INVALID_RESPONSE");
    return profile;
  }

  async submitReport(
    matchId: string,
    targetSeat: 0 | 1 | 2 | 3,
    reason: ReportReason,
    details: string,
  ): Promise<void> {
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
        matchId,
      )
    ) {
      throw new PlayerDataApiError("举报牌局编号无效", "INVALID_REQUEST");
    }
    const cleanDetails = details.normalize("NFKC").trim();
    if (!reportReasons.includes(reason) || cleanDetails.length > 500) {
      throw new PlayerDataApiError("举报内容无效", "INVALID_REQUEST");
    }
    const envelope = requireRecord(
      await this.request("/me/reports", {
        method: "POST",
        body: JSON.stringify({
          matchId,
          targetSeat,
          reason,
          ...(cleanDetails ? { details: cleanDetails } : {}),
        }),
      }),
      "举报响应",
    );
    const report = requireRecord(envelope.report, "举报记录");
    const statuses = ["open", "reviewing", "resolved", "dismissed"] as const;
    if (
      typeof report.status !== "string" ||
      !statuses.includes(report.status as (typeof statuses)[number]) ||
      typeof report.id !== "string" ||
      report.id.length < 1 ||
      report.id.length > 128
    ) {
      invalid("举报响应无效");
    }
  }

  private async request(
    path: string,
    init: RequestInit,
    signal?: AbortSignal,
  ): Promise<unknown> {
    let response: Response;
    try {
      response = await this.fetcher(
        `${this.baseUrl.replace(/\/$/u, "")}${path}`,
        {
          ...init,
          headers: {
            authorization: `Bearer ${this.token}`,
            ...(init.body ? { "content-type": "application/json" } : {}),
          },
          ...(signal ? { signal } : {}),
        },
      );
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError")
        throw cause;
      throw new PlayerDataApiError("联网数据服务暂时不可用", "UNAVAILABLE");
    }
    if (!response.ok) {
      const reporting = path === "/me/reports";
      if (response.status === 401)
        throw new PlayerDataApiError(
          "登录已失效，请重新登录",
          "UNAUTHENTICATED",
          401,
        );
      if (response.status === 403)
        throw new PlayerDataApiError(
          reporting
            ? "仅牌局参与者可以举报本局真人玩家"
            : "仅牌局参与者可以读取该回放",
          "FORBIDDEN",
          403,
        );
      if (response.status === 404)
        throw new PlayerDataApiError(
          reporting ? "已结算牌局不存在" : "权威回放不存在",
          "NOT_FOUND",
          404,
        );
      if (response.status === 400)
        throw new PlayerDataApiError(
          reporting
            ? "举报内容无效，请检查原因和补充说明"
            : "角色资料无效，请检查昵称与头像选项",
          "INVALID_REQUEST",
          400,
        );
      throw new PlayerDataApiError(
        "联网数据服务暂时不可用",
        "UNAVAILABLE",
        response.status,
      );
    }
    try {
      return await response.json();
    } catch {
      throw new PlayerDataApiError(
        "联网战绩响应不是有效 JSON",
        "INVALID_RESPONSE",
        response.status,
      );
    }
  }
}

export function createPlayerDataClient(): PlayerDataClient | null {
  const baseUrl = import.meta.env.VITE_GAME_API_URL as string | undefined;
  const token = getSessionToken();
  return baseUrl && token ? new PlayerDataClient(baseUrl, token) : null;
}

export function parsePlayerHistory(value: unknown): AuthoritativePlayerHistory {
  const envelope = requireRecord(value, "战绩响应");
  if (!Array.isArray(envelope.matches)) invalid("战绩响应缺少 matches 数组");
  const seen = new Set<string>();
  const matches = envelope.matches.map((item, index) => {
    const row = requireRecord(item, `第 ${index + 1} 条战绩`);
    const matchId = requireBoundedString(row.matchId, "matchId", 64);
    const roomId = requireBoundedString(row.roomId, "roomId", 64);
    if (seen.has(matchId)) invalid("战绩响应包含重复牌局");
    seen.add(matchId);
    const startedAt = requireIsoTimestamp(row.startedAt, "startedAt");
    const endedAt = requireIsoTimestamp(row.endedAt, "endedAt");
    if (endedAt < startedAt) invalid("战绩结束时间早于开始时间");
    const seat = requireInteger(row.seat, "seat", 0, 3) as 0 | 1 | 2 | 3;
    const team = requireInteger(row.team, "team", 0, 1) as 0 | 1;
    if (team !== seat % 2) invalid("战绩队伍与座位不一致");
    const finishOrder = requireInteger(row.finishOrder, "finishOrder", 1, 4) as
      1 | 2 | 3 | 4;
    const levelDelta = requireInteger(row.levelDelta, "levelDelta", 0, 3);
    const score = requireInteger(row.score, "score", -1_000_000, 1_000_000);
    const metrics = parseHistoryMetrics(row.metrics);
    if (
      (metrics.doubleUp && !metrics.won) ||
      (metrics.doubleDown && metrics.won) ||
      (metrics.doubleUp && metrics.doubleDown) ||
      metrics.won !== levelDelta > 0
    ) {
      invalid("战绩胜负指标互相矛盾");
    }
    return {
      matchId,
      roomId,
      startedAt,
      endedAt,
      seat,
      team,
      finishOrder,
      levelDelta,
      score,
      metrics,
    };
  });
  const sorted = matches.sort((left, right) => right.endedAt - left.endedAt);
  const summary = parseHistorySummary(envelope.summary);
  if (sorted.length > 0 && (!summary || summary.games < sorted.length))
    invalid("战绩汇总与对局数量不一致");
  return { matches: sorted, summary };
}

/**
 * Behavior rates are match based: a match counts once when at least one action
 * was managed by the trustee, or when at least one disconnect was recorded.
 */
export function deriveHistoryBehaviorRates(
  matches: readonly AuthoritativeMatchHistory[],
): AuthoritativeBehaviorRates {
  const games = matches.length;
  const trusteeGames = matches.filter(
    (match) => match.metrics.managedTurns > 0,
  ).length;
  const disconnectedGames = matches.filter(
    (match) => match.metrics.disconnects > 0,
  ).length;
  return {
    games,
    trusteeGames,
    disconnectedGames,
    trusteeMatchRate: games ? trusteeGames / games : 0,
    disconnectMatchRate: games ? disconnectedGames / games : 0,
  };
}

export function parsePlayerProfile(value: unknown): RemotePlayerProfile | null {
  const envelope = requireRecord(value, "角色档案响应");
  if (envelope.profile === null) return null;
  const profile = requireRecord(envelope.profile, "角色档案");
  const email = requireBoundedString(profile.email, "profile.email", 254)
    .trim()
    .toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email))
    invalid("profile.email 不是有效邮箱");
  return {
    email,
    nickname: parseProfileNickname(profile.nickname),
    avatar: parseProfileAvatar(profile.avatar),
    level: requireInteger(
      profile.level,
      "profile.level",
      1,
      Number.MAX_SAFE_INTEGER,
    ),
    experience: requireInteger(
      profile.experience,
      "profile.experience",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    reputation: requireInteger(
      profile.reputation,
      "profile.reputation",
      0,
      100,
    ),
  };
}

export function parseCompletedMatchReplay(
  value: unknown,
): CompletedMatchReplay {
  const envelope = requireRecord(value, "回放响应");
  const matchId = requireBoundedString(envelope.matchId, "matchId", 64);
  if (envelope.final !== true || envelope.participantAuthorized !== true)
    invalid("回放尚未最终结算或未获参与者授权");
  const checksum = requireBoundedString(envelope.checksum, "checksum", 256);
  const manifest = requireRecord(envelope.manifest, "replay manifest");
  if (manifest.matchId !== matchId || manifest.final !== true)
    invalid("回放 manifest 与权威响应不一致");
  if (!Array.isArray(manifest.events)) invalid("回放 manifest 缺少事件数组");
  const events = manifest.events.map(parseReplayEvent);
  for (let index = 1; index < events.length; index += 1) {
    if ((events[index - 1]?.seq ?? -1) >= (events[index]?.seq ?? -1))
      invalid("回放事件序号不是严格递增");
  }
  if (!Array.isArray(manifest.players) || manifest.players.length !== 4)
    invalid("回放缺少四个权威座位");
  const players = manifest.players.map(parseReplayPlayer);
  if (new Set(players.map((player) => player.seat)).size !== 4)
    invalid("回放座位重复");
  const revealedHandsByRound =
    parseRevealedHandsByRound(manifest.revealedHandsByRound) ??
    legacyRevealedHandsByRound(manifest.revealedHands, events);
  return {
    matchId,
    events,
    players,
    ...(revealedHandsByRound ? { revealedHandsByRound } : {}),
    checksum,
  };
}

export function createRemoteReplayPresentation(
  history: AuthoritativeMatchHistory,
  replay: CompletedMatchReplay,
): ReplayPresentation {
  if (history.matchId !== replay.matchId)
    throw new PlayerDataApiError(
      "回放与战绩不属于同一牌局",
      "INVALID_RESPONSE",
    );
  const finishOrder = deriveFinishOrder(replay.events);
  const participants = replay.players.map(
    (player): ReplayParticipantPresentation => ({
      seat: player.seat,
      nickname: player.isBot
        ? `${player.nickname} · AI${botLevelLabel(player.botLevel)}`
        : player.nickname,
      team: player.team,
      finishOrder: finishOrder.get(player.seat) ?? null,
      isBot: player.isBot,
    }),
  );
  return {
    source: "remote",
    matchId: history.matchId,
    selfSeat: history.seat,
    endedAt: history.endedAt,
    participants,
    authorityEvents: replay.events,
    fallbackEvents: [],
    ...(replay.revealedHandsByRound
      ? { revealedHandsByRound: replay.revealedHandsByRound }
      : {}),
    checksum: replay.checksum,
  };
}

function parseHistoryMetrics(value: unknown): AuthoritativeHistoryMetrics {
  const metrics = requireRecord(value, "metrics");
  if (
    typeof metrics.won !== "boolean" ||
    typeof metrics.doubleUp !== "boolean" ||
    typeof metrics.doubleDown !== "boolean" ||
    typeof metrics.disconnected !== "boolean"
  ) {
    invalid("战绩 metrics 缺少权威布尔值");
  }
  const parsed = {
    won: metrics.won,
    doubleUp: metrics.doubleUp,
    doubleDown: metrics.doubleDown,
    plays: requireInteger(
      metrics.plays,
      "metrics.plays",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    bombsPlayed: requireInteger(
      metrics.bombsPlayed,
      "metrics.bombsPlayed",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    trickRights: requireInteger(
      metrics.trickRights,
      "metrics.trickRights",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    managedTurns: requireInteger(
      metrics.managedTurns,
      "metrics.managedTurns",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    trusteeSeconds: requireInteger(
      metrics.trusteeSeconds,
      "metrics.trusteeSeconds",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    disconnects: requireInteger(
      metrics.disconnects,
      "metrics.disconnects",
      0,
      Number.MAX_SAFE_INTEGER,
    ),
    disconnected: metrics.disconnected,
  };
  if (parsed.disconnected !== parsed.disconnects > 0)
    invalid("断线布尔值与次数不一致");
  return parsed;
}

function parseHistorySummary(
  value: unknown,
): AuthoritativeHistorySummary | null {
  if (value === null) return null;
  const summary = requireRecord(value, "summary");
  if (!Array.isArray(summary.recentTrend) || summary.recentTrend.length > 10)
    invalid("近期趋势格式无效");
  const metrics = requireRecord(summary.metrics, "summary.metrics");
  const currentRating = requireInteger(
    summary.currentRating,
    "summary.currentRating",
    0,
    Number.MAX_SAFE_INTEGER,
  );
  const peakRating = requireInteger(
    summary.peakRating,
    "summary.peakRating",
    0,
    Number.MAX_SAFE_INTEGER,
  );
  if (peakRating < currentRating) invalid("最高积分不能低于当前积分");
  return {
    games: requireInteger(
      summary.games,
      "summary.games",
      1,
      Number.MAX_SAFE_INTEGER,
    ),
    currentRating,
    peakRating,
    currentRank: requireBoundedString(
      summary.currentRank,
      "summary.currentRank",
      16,
    ),
    highestRank: requireBoundedString(
      summary.highestRank,
      "summary.highestRank",
      16,
    ),
    recentTrend: summary.recentTrend.map((score) =>
      requireInteger(score, "summary.recentTrend", -1_000_000, 1_000_000),
    ),
    metrics: {
      plays: requireInteger(
        metrics.plays,
        "summary.metrics.plays",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      bombsPlayed: requireInteger(
        metrics.bombsPlayed,
        "summary.metrics.bombsPlayed",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      trickRights: requireInteger(
        metrics.trickRights,
        "summary.metrics.trickRights",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      managedTurns: requireInteger(
        metrics.managedTurns,
        "summary.metrics.managedTurns",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      trusteeSeconds: requireInteger(
        metrics.trusteeSeconds,
        "summary.metrics.trusteeSeconds",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      disconnects: requireInteger(
        metrics.disconnects,
        "summary.metrics.disconnects",
        0,
        Number.MAX_SAFE_INTEGER,
      ),
    },
  };
}

function parseReplayEvent(
  value: unknown,
  index: number,
): ReplayEventPresentation {
  const event = requireRecord(value, `第 ${index + 1} 个回放事件`);
  return {
    seq: requireInteger(event.seq, "event.seq", 0, Number.MAX_SAFE_INTEGER),
    type: requireBoundedString(event.type, "event.type", 64),
    payload: requireRecord(event.payload, "event.payload"),
    createdAt: requireTimestamp(event.createdAt, "event.createdAt"),
  };
}

function parseReplayPlayer(
  value: unknown,
  index: number,
): ReplayPlayerMetadata {
  const player = requireRecord(value, `第 ${index + 1} 个回放座位`);
  const seat = requireInteger(player.seat, "player.seat", 0, 3) as
    0 | 1 | 2 | 3;
  const team = requireInteger(player.team, "player.team", 0, 1) as 0 | 1;
  if (team !== seat % 2 || typeof player.isBot !== "boolean")
    invalid("回放座位队伍或 AI 标记无效");
  const botLevel = player.botLevel;
  if (
    botLevel !== null &&
    botLevel !== "standard" &&
    botLevel !== "expert" &&
    botLevel !== "master"
  )
    invalid("回放 AI 难度无效");
  const userId = player.userId;
  if (
    player.isBot
      ? userId !== null || botLevel === null
      : typeof userId !== "string" || userId.length === 0 || botLevel !== null
  ) {
    invalid("回放座位身份元数据无效");
  }
  const nickname = requireBoundedString(player.nickname, "player.nickname", 24);
  return {
    userId: userId as string | null,
    nickname,
    seat,
    team,
    isBot: player.isBot,
    botLevel,
  };
}

function parseRevealedHands(value: unknown): RevealedHands | undefined {
  if (value === null || value === undefined) return undefined;
  const record = requireRecord(value, "revealedHands");
  const keys = Object.keys(record).sort();
  if (keys.length !== 4 || keys.some((key, index) => key !== String(index)))
    invalid("结算手牌必须覆盖四个座位");
  return Object.fromEntries(
    keys.map((key) => {
      const cards = record[key];
      if (
        !Array.isArray(cards) ||
        !cards.every((card) => typeof card === "string" && card.length > 0)
      )
        invalid("结算手牌包含无效实体牌");
      if (new Set(cards).size !== cards.length)
        invalid("单个座位的结算手牌包含重复实体牌");
      return [key, cards.map((card) => String(card))];
    }),
  ) as RevealedHands;
}

function parseRevealedHandsByRound(
  value: unknown,
): RevealedHandsByRound | undefined {
  if (value === null || value === undefined) return undefined;
  const record = requireRecord(value, "revealedHandsByRound");
  const keys = Object.keys(record).sort(
    (left, right) => Number(left) - Number(right),
  );
  if (
    keys.length === 0 ||
    keys.length > 32 ||
    keys.some((key, index) => key !== String(index + 1))
  ) {
    invalid("按轮结算手牌必须从第 1 轮连续覆盖");
  }
  return Object.fromEntries(
    keys.map((key) => {
      const hands = parseRevealedHands(record[key]);
      if (!hands) invalid(`第 ${key} 轮缺少四家完整手牌`);
      return [key, hands];
    }),
  );
}

function legacyRevealedHandsByRound(
  value: unknown,
  events: readonly ReplayEventPresentation[],
): RevealedHandsByRound | undefined {
  const hands = parseRevealedHands(value);
  if (!hands) return undefined;
  let lastRound = 1;
  for (const event of events) {
    if (event.type !== "ROUND_STARTED" && event.type !== "CARDS_DEALT")
      continue;
    const roundNumber = event.payload.roundNumber;
    if (
      typeof roundNumber === "number" &&
      Number.isInteger(roundNumber) &&
      roundNumber > lastRound
    )
      lastRound = roundNumber;
  }
  return { [String(lastRound)]: hands };
}

function deriveFinishOrder(
  events: readonly ReplayEventPresentation[],
): ReadonlyMap<number, 1 | 2 | 3 | 4> {
  for (const event of [...events].reverse()) {
    const payloadSettlement =
      event.type === "MATCH_SETTLED"
        ? optionalRecord(event.payload.settlement)
        : null;
    const round = optionalRecord(payloadSettlement?.round);
    const roundSettlement =
      event.type === "ROUND_SETTLED"
        ? optionalRecord(event.payload.settlement)
        : null;
    const candidate = round?.finishOrder ?? roundSettlement?.finishOrder;
    if (
      !Array.isArray(candidate) ||
      candidate.length !== 4 ||
      new Set(candidate).size !== 4 ||
      !candidate.every(
        (seat) =>
          Number.isInteger(seat) && Number(seat) >= 0 && Number(seat) <= 3,
      )
    )
      continue;
    return new Map(
      candidate.map((seat, index) => [
        Number(seat),
        (index + 1) as 1 | 2 | 3 | 4,
      ]),
    );
  }
  const result = new Map<number, 1 | 2 | 3 | 4>();
  for (const event of events) {
    if (event.type !== "PLAYER_FINISHED") continue;
    const seat = event.payload.seat;
    const order = event.payload.order;
    if (
      Number.isInteger(seat) &&
      Number(seat) >= 0 &&
      Number(seat) <= 3 &&
      Number.isInteger(order) &&
      Number(order) >= 1 &&
      Number(order) <= 4
    ) {
      result.set(Number(seat), Number(order) as 1 | 2 | 3 | 4);
    }
  }
  return result;
}

function botLevelLabel(level: ReplayPlayerMetadata["botLevel"]): string {
  if (level === "standard") return "（标准）";
  if (level === "expert") return "（高手）";
  if (level === "master") return "（大师）";
  return "";
}

function parseProfileNickname(value: unknown): string {
  if (typeof value !== "string") invalid("昵称必须是文字");
  const nickname = value.normalize("NFKC").trim();
  const length = graphemeLength(nickname);
  const hasControlCharacter = Array.from(nickname).some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code <= 31 || code === 127;
  });
  if (length < 2 || length > 12 || hasControlCharacter)
    invalid("昵称须为 2—12 个字符");
  return nickname;
}

function parseProfileAvatar(value: unknown): AvatarConfig {
  const input = requireRecord(value, "profile.avatar");
  const expectedKeys = [
    "accessory",
    "brows",
    "eyes",
    "face",
    "facialDetail",
    "gender",
    "hair",
    "hairColor",
    "idle",
    "mouth",
    "nose",
    "outfit",
    "shoes",
    "skin",
    "version",
  ];
  const actualKeys = Object.keys(input).sort();
  const expectedWithPortrait = [...expectedKeys, "customPortraitId"].sort();
  if (
    (actualKeys.join("\0") !== expectedKeys.join("\0") &&
      actualKeys.join("\0") !== expectedWithPortrait.join("\0")) ||
    input.version !== 1 ||
    (input.gender !== "woman" && input.gender !== "man")
  ) {
    invalid("角色配置字段、版本或性别无效");
  }
  const stringField = (key: keyof AvatarConfig): string => {
    const field = input[key];
    if (typeof field !== "string" || field.length < 1 || field.length > 64)
      invalid(`角色配置 ${key} 无效`);
    return field;
  };
  const avatar: AvatarConfig = {
    version: 1,
    gender: input.gender,
    face: stringField("face"),
    skin: profileAllowed(
      stringField("skin"),
      ["skin-warm-1", "skin-warm-2", "skin-warm-3", "skin-neutral"],
      "skin",
    ),
    eyes: profileAllowed(
      stringField("eyes"),
      ["eyes-clear", "eyes-soft", "eyes-bright"],
      "eyes",
    ),
    brows: profileAllowed(
      stringField("brows"),
      ["brows-natural", "brows-straight", "brows-defined"],
      "brows",
    ),
    nose: profileAllowed(
      stringField("nose"),
      ["nose-soft", "nose-defined", "nose-round"],
      "nose",
    ),
    mouth: profileAllowed(
      stringField("mouth"),
      ["mouth-smile", "mouth-calm", "mouth-bright"],
      "mouth",
    ),
    hair: stringField("hair"),
    hairColor: stringField("hairColor"),
    facialDetail: profileAllowed(
      stringField("facialDetail"),
      ["detail-soft-blush", "detail-freckle", "detail-clean"],
      "facialDetail",
    ),
    outfit: stringField("outfit"),
    shoes: profileAllowed(
      stringField("shoes"),
      ["shoes-modern-loafer", "shoes-sneaker", "shoes-derby"],
      "shoes",
    ),
    accessory: stringField("accessory"),
    idle: stringField("idle"),
    ...(input.customPortraitId === undefined
      ? {}
      : { customPortraitId: stringField("customPortraitId") }),
  };
  if (
    avatar.customPortraitId !== undefined &&
    !/^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(avatar.customPortraitId)
  )
    invalid("自定义头像编号无效");
  if (!/^#[0-9a-f]{6}$/iu.test(avatar.hairColor)) invalid("角色发色无效");
  try {
    return structuredClone(validateAvatarConfig(avatar));
  } catch {
    invalid("角色外观选项不在当前目录中");
  }
}

function profileAllowed<const Item extends string>(
  value: string,
  values: readonly Item[],
  label: string,
): Item {
  if (!(values as readonly string[]).includes(value))
    invalid(`角色配置 ${label} 无效`);
  return value as Item;
}

function requireRecord(
  value: unknown,
  label: string,
): Readonly<Record<string, unknown>> {
  const record = optionalRecord(value);
  if (!record) invalid(`${label}不是对象`);
  return record;
}

function optionalRecord(
  value: unknown,
): Readonly<Record<string, unknown>> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function requireBoundedString(
  value: unknown,
  label: string,
  maximum: number,
): string {
  if (typeof value !== "string" || value.length === 0 || value.length > maximum)
    invalid(`${label}不是有效字符串`);
  return value;
}

function requireInteger(
  value: unknown,
  label: string,
  minimum: number,
  maximum: number,
): number {
  if (
    !Number.isSafeInteger(value) ||
    Number(value) < minimum ||
    Number(value) > maximum
  )
    invalid(`${label}不是范围内整数`);
  return Number(value);
}

function requireIsoTimestamp(value: unknown, label: string): number {
  if (typeof value !== "string") invalid(`${label}不是 ISO 时间`);
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) invalid(`${label}不是有效时间`);
  return timestamp;
}

function requireTimestamp(value: unknown, label: string): number {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0)
    return value;
  return requireIsoTimestamp(value, label);
}

function invalid(message: string): never {
  throw new PlayerDataApiError(message, "INVALID_RESPONSE");
}

function graphemeLength(value: string): number {
  return Array.from(
    new Intl.Segmenter("zh-CN", { granularity: "grapheme" }).segment(value),
  ).length;
}
